import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as api from '../../api/PurchaseAPI/storeClosingAPI';

const getErrorMessage = (err, fallback) =>
    err?.ErrorDetails?.ErrorMessage || err?.Message || err?.message || fallback;

// ── Async Thunks ─────────────────────────────────────────────────────────────

export const fetchClosingTypes = createAsyncThunk(
    'storeClosing/fetchClosingTypes',
    async (_, { rejectWithValue }) => {
        try {
            const res = await api.getStoreClosingType();
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load closing types'));
        }
    }
);

export const fetchClosingCCs = createAsyncThunk(
    'storeClosing/fetchClosingCCs',
    async ({ closingType, uid, rid }, { rejectWithValue }) => {
        try {
            const res = await api.getStoreClosingCCs(closingType, uid, rid);
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load cost centers'));
        }
    }
);

// Combines the two "can this CC be closed?" checks the legacy page fires whenever
// a cost center is selected — pending workflow approvals and leftover stock balance.
// Either coming back non-empty blocks submission (mirrors GetClosingCCData()).
export const fetchPendingCheck = createAsyncThunk(
    'storeClosing/fetchPendingCheck',
    async (cc, { rejectWithValue }) => {
        try {
            const [pendingsRes, balanceRes] = await Promise.all([
                api.getCCStorePendings(cc),
                api.getCCBalanceStock(cc),
            ]);
            return {
                pendings: pendingsRes?.Data || [],
                balanceStock: balanceRes?.Data || [],
            };
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to check pending items'));
        }
    }
);

// Success format is "Submitted".
export const submitStoreClosing = createAsyncThunk(
    'storeClosing/submit',
    async (payload, { rejectWithValue }) => {
        try {
            const res = await api.saveStoreClosingData(payload);
            const raw = res?.Data || '';
            if (raw !== 'Submitted') {
                return rejectWithValue(raw || 'Failed to submit');
            }
            return true;
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to submit'));
        }
    }
);

// ── Slice ────────────────────────────────────────────────────────────────────

const initialState = {
    closingTypeList: [],
    ccList: [],
    pendings: [],
    balanceStock: [],
    submitResult: null,
    loading: {
        closingTypes: false,
        ccList: false,
        pendingCheck: false,
        submit: false,
    },
    errors: {
        closingTypes: null,
        ccList: null,
        pendingCheck: null,
        submit: null,
    },
};

const storeClosingSlice = createSlice({
    name: 'storeClosing',
    initialState,
    reducers: {
        clearCCList:      (s) => { s.ccList = []; },
        clearPendingCheck:(s) => { s.pendings = []; s.balanceStock = []; s.errors.pendingCheck = null; },
        clearSubmitResult:(s) => { s.submitResult = null; s.errors.submit = null; },
        resetStoreClosing: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchClosingTypes.pending,   (s) => { s.loading.closingTypes = true; s.errors.closingTypes = null; })
            .addCase(fetchClosingTypes.fulfilled, (s, a) => { s.loading.closingTypes = false; s.closingTypeList = a.payload; })
            .addCase(fetchClosingTypes.rejected,  (s, a) => { s.loading.closingTypes = false; s.errors.closingTypes = a.payload; });

        builder
            .addCase(fetchClosingCCs.pending,   (s) => { s.loading.ccList = true; s.errors.ccList = null; })
            .addCase(fetchClosingCCs.fulfilled, (s, a) => { s.loading.ccList = false; s.ccList = a.payload; })
            .addCase(fetchClosingCCs.rejected,  (s, a) => { s.loading.ccList = false; s.errors.ccList = a.payload; });

        builder
            .addCase(fetchPendingCheck.pending,   (s) => { s.loading.pendingCheck = true; s.errors.pendingCheck = null; })
            .addCase(fetchPendingCheck.fulfilled, (s, a) => {
                s.loading.pendingCheck = false;
                s.pendings = a.payload.pendings;
                s.balanceStock = a.payload.balanceStock;
            })
            .addCase(fetchPendingCheck.rejected,  (s, a) => { s.loading.pendingCheck = false; s.errors.pendingCheck = a.payload; });

        builder
            .addCase(submitStoreClosing.pending,   (s) => { s.loading.submit = true; s.errors.submit = null; s.submitResult = null; })
            .addCase(submitStoreClosing.fulfilled, (s) => { s.loading.submit = false; s.submitResult = true; })
            .addCase(submitStoreClosing.rejected,  (s, a) => { s.loading.submit = false; s.errors.submit = a.payload; });
    },
});

export const {
    clearCCList,
    clearPendingCheck,
    clearSubmitResult,
    resetStoreClosing,
} = storeClosingSlice.actions;

// ── Selectors ────────────────────────────────────────────────────────────────
export const selectSCClosingTypeList = (s) => s.storeClosing.closingTypeList;
export const selectSCCCList          = (s) => s.storeClosing.ccList;
export const selectSCPendings        = (s) => s.storeClosing.pendings;
export const selectSCBalanceStock    = (s) => s.storeClosing.balanceStock;
export const selectSCSubmitResult    = (s) => s.storeClosing.submitResult;
export const selectSCLoading         = (s) => s.storeClosing.loading;
export const selectSCErrors          = (s) => s.storeClosing.errors;

export default storeClosingSlice.reducer;
